import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Calendar, ChevronDown, TrendingUp, SlidersHorizontal, X, ChevronLeft, ChevronRight, Locate } from 'lucide-react';
// Declare Leaflet types
declare global {
  interface Window {
    L: any;
  }
}
export function Events() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [showDistanceDropdown, setShowDistanceDropdown] = useState(false);
  const [showTerrainDropdown, setShowTerrainDropdown] = useState(false);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const [selectedDistance, setSelectedDistance] = useState('All Distances');
  const [selectedTerrain, setSelectedTerrain] = useState('All Terrains');
  const [selectedPrice, setSelectedPrice] = useState('All Prices');
  const [locationSearch, setLocationSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const distanceRef = useRef<HTMLDivElement>(null);
  const terrainRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  const locations = ['San Diego, CA', 'Austin, TX', 'Denver, CO', 'Chicago, IL', 'New York, NY', 'Seattle, WA', 'Los Angeles, CA', 'Boston, MA', 'Portland, OR', 'Miami, FL', 'San Francisco, CA', 'Phoenix, AZ', 'Dallas, TX', 'Atlanta, GA'];
  const distances = ['All Distances', '5K', '10K', 'Half Marathon', 'Marathon', 'Ultra Marathon'];
  const terrains = ['All Terrains', 'Road', 'Trail', 'Track', 'Mixed'];
  const prices = ['All Prices', 'Free', 'Under $25', '$25-$50', '$50-$100', 'Over $100'];
  const filteredLocations = locations.filter(loc => loc.toLowerCase().includes(locationSearch.toLowerCase()));
  // Get user's location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      }, error => {
        console.log('Location access denied, using default location');
        // Default to center of US if location denied
        setUserLocation({
          lat: 39.8283,
          lng: -98.5795
        });
      });
    } else {
      // Default location if geolocation not supported
      setUserLocation({
        lat: 39.8283,
        lng: -98.5795
      });
    }
  }, []);
  // Initialize Leaflet map
  useEffect(() => {
    if (!mapRef.current || !userLocation || mapInstanceRef.current) return;
    // Check if Leaflet CSS is already loaded
    const existingLink = document.querySelector('link[href*="leaflet.css"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      link.crossOrigin = '';
      document.head.appendChild(link);
    }
    // Check if Leaflet JS is already loaded
    if (window.L) {
      initializeMap();
    } else {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.async = true;
      script.onload = () => {
        initializeMap();
      };
      script.onerror = () => {
        console.error('Failed to load Leaflet');
        setMapLoaded(true); // Set to true to hide loading state
      };
      document.head.appendChild(script);
    }
    function initializeMap() {
      if (!mapRef.current || mapInstanceRef.current || !window.L) return;
      try {
        const L = window.L;
        // Initialize map
        const map = L.map(mapRef.current, {
          center: [userLocation.lat, userLocation.lng],
          zoom: 10,
          zoomControl: true,
          scrollWheelZoom: false
        });
        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19
        }).addTo(map);
        // Custom icon for user location
        const userIcon = L.divIcon({
          className: 'custom-user-marker',
          html: `<div style="
            width: 20px;
            height: 20px;
            background: #01AAE7;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          "></div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        });
        // Add user location marker
        L.marker([userLocation.lat, userLocation.lng], {
          icon: userIcon
        }).addTo(map).bindPopup('<strong>Your Location</strong>');
        // Add event markers (example locations around user)
        const eventLocations = [{
          lat: userLocation.lat + 0.05,
          lng: userLocation.lng + 0.05,
          count: 3
        }, {
          lat: userLocation.lat - 0.03,
          lng: userLocation.lng + 0.02,
          count: 5
        }, {
          lat: userLocation.lat + 0.02,
          lng: userLocation.lng - 0.04,
          count: 2
        }];
        eventLocations.forEach(location => {
          const eventIcon = L.divIcon({
            className: 'custom-event-marker',
            html: `<div style="
              width: 32px;
              height: 32px;
              background: #0E578E;
              border: 2px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 14px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            ">${location.count}</div>`,
            iconSize: [32, 32],
            iconAnchor: [16, 16]
          });
          L.marker([location.lat, location.lng], {
            icon: eventIcon
          }).addTo(map).bindPopup(`<strong>${location.count} events</strong><br/>Click to view`);
        });
        mapInstanceRef.current = map;
        setMapLoaded(true);
        // Force map to resize after a short delay
        setTimeout(() => {
          map.invalidateSize();
        }, 100);
      } catch (error) {
        console.error('Error initializing map:', error);
        setMapLoaded(true);
      }
    }
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [userLocation]);
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
      if (distanceRef.current && !distanceRef.current.contains(event.target as Node)) {
        setShowDistanceDropdown(false);
      }
      if (terrainRef.current && !terrainRef.current.contains(event.target as Node)) {
        setShowTerrainDropdown(false);
      }
      if (priceRef.current && !priceRef.current.contains(event.target as Node)) {
        setShowPriceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const allEvents = [{
    id: 1,
    title: 'Coastal Marathon 2024',
    date: '2024-03-15',
    displayDate: 'March 15, 2024',
    location: 'San Diego, CA',
    distance: 'Marathon',
    terrain: 'Road',
    price: '$50-$100',
    image: 'https://images.unsplash.com/photo-1532444458054-01a7dd3e9fca?w=800&h=600&fit=crop&auto=format&q=80'
  }, {
    id: 2,
    title: 'City Park 5K',
    date: '2024-03-22',
    displayDate: 'March 22, 2024',
    location: 'Austin, TX',
    distance: '5K',
    terrain: 'Road',
    price: 'Under $25',
    image: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=800&h=600&fit=crop&auto=format&q=80'
  }, {
    id: 3,
    title: 'Mountain Trail Ultra',
    date: '2024-04-05',
    displayDate: 'April 5, 2024',
    location: 'Denver, CO',
    distance: 'Ultra Marathon',
    terrain: 'Trail',
    price: 'Over $100',
    image: 'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=800&h=600&fit=crop&auto=format&q=80'
  }, {
    id: 4,
    title: 'Spring Half Marathon',
    date: '2024-04-12',
    displayDate: 'April 12, 2024',
    location: 'Chicago, IL',
    distance: 'Half Marathon',
    terrain: 'Road',
    price: '$25-$50',
    image: 'https://images.unsplash.com/photo-1513593771513-7b58b6c4af38?w=800&h=600&fit=crop&auto=format&q=80'
  }, {
    id: 5,
    title: 'Midnight Run',
    date: '2024-04-20',
    displayDate: 'April 20, 2024',
    location: 'New York, NY',
    distance: '10K',
    terrain: 'Road',
    price: 'Free',
    image: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&h=600&fit=crop&auto=format&q=80'
  }, {
    id: 6,
    title: 'Charity Fun Run',
    date: '2024-05-01',
    displayDate: 'May 1, 2024',
    location: 'Seattle, WA',
    distance: '5K',
    terrain: 'Mixed',
    price: 'Under $25',
    image: 'https://images.unsplash.com/photo-1551958219-acbc608c6377?w=800&h=600&fit=crop&auto=format&q=80'
  }];
  const filteredEvents = allEvents.filter(event => {
    const matchesSearch = searchQuery === '' || event.title.toLowerCase().includes(searchQuery.toLowerCase()) || event.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = selectedLocation === '' || event.location === selectedLocation;
    const matchesDate = selectedDate === '' || event.date === selectedDate;
    const matchesDistance = selectedDistance === 'All Distances' || event.distance === selectedDistance;
    const matchesTerrain = selectedTerrain === 'All Terrains' || event.terrain === selectedTerrain;
    const matchesPrice = selectedPrice === 'All Prices' || event.price === selectedPrice;
    return matchesSearch && matchesLocation && matchesDate && matchesDistance && matchesTerrain && matchesPrice;
  });
  const handleLocationSelect = (location: string) => {
    setSelectedLocation(location);
    setLocationSearch(location);
    setShowLocationDropdown(false);
  };
  const clearLocation = () => {
    setSelectedLocation('');
    setLocationSearch('');
  };
  const clearDate = () => {
    setSelectedDate('');
  };
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedLocation('');
    setLocationSearch('');
    setSelectedDate('');
    setSelectedDistance('All Distances');
    setSelectedTerrain('All Terrains');
    setSelectedPrice('All Prices');
  };
  const activeFiltersCount = [selectedLocation, selectedDate, selectedDistance !== 'All Distances', selectedTerrain !== 'All Terrains', selectedPrice !== 'All Prices'].filter(Boolean).length;
  return <div className="w-full bg-gray-50 min-h-screen pb-20" data-id="element-811">
      {/* Banner */}
      <div className="relative h-[300px] sm:h-[400px] overflow-hidden" data-id="element-812">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1452626038306-9aae5e071dd3?w=1920&h=400&fit=crop&auto=format&q=80)'
      }} data-id="element-813">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E578E]/95 via-[#0E578E]/90 to-[#01AAE7]/85" data-id="element-814"></div>
        </div>

        <div className="absolute inset-0" data-id="element-815">
          <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} data-id="element-816"></div>
        </div>

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-center" data-id="element-817">
          <div className="text-center max-w-3xl" data-id="element-818">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold mb-4 sm:mb-6 border border-white/30 shadow-lg" data-id="element-819">
              <TrendingUp className="h-3.5 sm:h-4 w-3.5 sm:w-4" data-id="element-820" />
              {filteredEvents.length} Events Available
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-3 sm:mb-4 leading-tight drop-shadow-lg px-4" data-id="element-821">
              Discover Your Next Challenge
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-white/95 leading-relaxed max-w-2xl mx-auto drop-shadow-md px-4" data-id="element-822">
              Browse thousands of running events worldwide. From local 5Ks to
              international marathons, find the perfect race for your goals.
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0" data-id="element-823">
          <svg viewBox="0 0 1440 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" data-id="element-824">
            <path d="M0 48H1440V24C1440 24 1200 0 720 0C240 0 0 24 0 24V48Z" fill="#F9FAFB" data-id="element-825" />
          </svg>
        </div>
      </div>

      {/* Enhanced Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-6 sm:mt-8" data-id="element-826">
        {/* Mobile: Only Filter Icon */}
        <div className="lg:hidden mb-6" data-id="element-827">
          <button onClick={() => setShowMobileFilters(!showMobileFilters)} className="relative w-full flex items-center justify-between gap-3 px-5 py-4 bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-[#01AAE7] transition-all" data-id="element-828">
            <div className="flex items-center gap-3" data-id="element-829">
              <div className="w-10 h-10 bg-gradient-to-br from-[#0E578E] to-[#01AAE7] rounded-lg flex items-center justify-center" data-id="element-830">
                <SlidersHorizontal className="h-5 w-5 text-white" data-id="element-831" />
              </div>
              <div className="text-left" data-id="element-832">
                <div className="text-sm font-bold text-gray-900" data-id="element-833">
                  Filters & Search
                </div>
                <div className="text-xs text-gray-500" data-id="element-834">
                  {activeFiltersCount > 0 ? `${activeFiltersCount} active` : 'Tap to filter'}
                </div>
              </div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} data-id="element-835" />
            {activeFiltersCount > 0 && <span className="absolute -top-2 -right-2 bg-[#01AAE7] text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-lg" data-id="element-836">
                {activeFiltersCount}
              </span>}
          </button>

          {/* Mobile Filter Dropdown */}
          {showMobileFilters && <div className="mt-4 bg-white rounded-xl shadow-xl border border-gray-200 p-5 space-y-4" data-id="element-837">
              {/* Search */}
              <div className="relative" data-id="element-838">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10" data-id="element-839" />
                <input type="text" placeholder="Search events..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#01AAE7] text-gray-900 transition-all" data-id="element-840" />
              </div>

              {/* Location */}
              <div className="relative" ref={locationRef} data-id="element-841">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10" data-id="element-842" />
                <input type="text" placeholder="Location" value={locationSearch} onChange={e => {
              setLocationSearch(e.target.value);
              setShowLocationDropdown(true);
              if (e.target.value === '') {
                setSelectedLocation('');
              }
            }} onFocus={() => setShowLocationDropdown(true)} className="w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#01AAE7] text-gray-900 transition-all" data-id="element-843" />
                {selectedLocation && <button onClick={() => {
              setSelectedLocation('');
              setLocationSearch('');
            }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10" data-id="element-844">
                    <X className="h-4 w-4" data-id="element-845" />
                  </button>}
              </div>

              {/* Date */}
              <div className="relative" data-id="element-846">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none z-10" data-id="element-847" />
                <input ref={dateInputRef} type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#01AAE7] text-gray-900 transition-all cursor-pointer" data-id="element-848" />
                {selectedDate && <button onClick={() => setSelectedDate('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10" data-id="element-849">
                    <X className="h-4 w-4" data-id="element-850" />
                  </button>}
              </div>

              {/* Advanced Filters Toggle */}
              <button onClick={() => setShowFiltersPanel(!showFiltersPanel)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all" data-id="element-851">
                <span className="text-sm font-semibold text-gray-700" data-id="element-852">
                  Advanced Filters
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showFiltersPanel ? 'rotate-180' : ''}`} data-id="element-853" />
              </button>

              {showFiltersPanel && <div className="space-y-3 pt-3 border-t border-gray-200" data-id="element-854">
                  {/* Distance, Terrain, Price dropdowns */}
                  <div className="relative" ref={distanceRef} data-id="element-855">
                    <button onClick={() => setShowDistanceDropdown(!showDistanceDropdown)} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-left flex items-center justify-between hover:border-[#01AAE7] transition-all" data-id="element-856">
                      <span className="text-sm font-medium text-gray-700" data-id="element-857">
                        {selectedDistance}
                      </span>
                      <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-858" />
                    </button>
                  </div>

                  <div className="relative" ref={terrainRef} data-id="element-859">
                    <button onClick={() => setShowTerrainDropdown(!showTerrainDropdown)} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-left flex items-center justify-between hover:border-[#01AAE7] transition-all" data-id="element-860">
                      <span className="text-sm font-medium text-gray-700" data-id="element-861">
                        {selectedTerrain}
                      </span>
                      <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-862" />
                    </button>
                  </div>

                  <div className="relative" ref={priceRef} data-id="element-863">
                    <button onClick={() => setShowPriceDropdown(!showPriceDropdown)} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-left flex items-center justify-between hover:border-[#01AAE7] transition-all" data-id="element-864">
                      <span className="text-sm font-medium text-gray-700" data-id="element-865">
                        {selectedPrice}
                      </span>
                      <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-866" />
                    </button>
                  </div>
                </div>}

              {/* Clear All & Search Buttons */}
              <div className="flex gap-3 pt-3 border-t border-gray-200" data-id="element-867">
                {activeFiltersCount > 0 && <button onClick={() => {
              setSearchQuery('');
              setSelectedLocation('');
              setLocationSearch('');
              setSelectedDate('');
              setSelectedDistance('All Distances');
              setSelectedTerrain('All Terrains');
              setSelectedPrice('All Prices');
            }} className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all" data-id="element-868">
                    Clear All
                  </button>}
                <button className="flex-1 px-4 py-3 bg-gradient-to-r from-[#01AAE7] to-[#0199d6] text-white font-bold rounded-xl hover:from-[#0199d6] hover:to-[#0188c5] transition-all shadow-lg" data-id="element-869">
                  Apply Filters
                </button>
              </div>
            </div>}
        </div>

        {/* Desktop: Full Filter Bar (unchanged) */}
        <div className="hidden lg:block bg-white rounded-2xl shadow-xl border border-gray-100 p-5 mb-10" data-id="element-870">
          <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center" data-id="element-871">
            <div className="relative flex-1" data-id="element-872">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10" data-id="element-873" />
              <input type="text" placeholder="Search events..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#01AAE7] text-gray-900 transition-all" data-id="element-874" />
            </div>

            <div className="relative lg:w-48" ref={locationRef} data-id="element-875">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10" data-id="element-876" />
              <input type="text" placeholder="Location" value={locationSearch} onChange={e => {
              setLocationSearch(e.target.value);
              setShowLocationDropdown(true);
              if (e.target.value === '') {
                setSelectedLocation('');
              }
            }} onFocus={() => setShowLocationDropdown(true)} className="w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#01AAE7] text-gray-900 transition-all" data-id="element-877" />
              {selectedLocation && <button onClick={() => {
              setSelectedLocation('');
              setLocationSearch('');
            }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10" data-id="element-878">
                  <X className="h-4 w-4" data-id="element-879" />
                </button>}

              {showLocationDropdown && locationSearch && filteredLocations.length > 0 && <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto" data-id="element-880">
                    {filteredLocations.map(location => <button key={location} onClick={() => {
                setSelectedLocation(location);
                setLocationSearch(location);
                setShowLocationDropdown(false);
              }} className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors text-gray-700 text-sm border-b border-gray-100 last:border-b-0 flex items-center gap-2" data-id="element-881">
                        <MapPin className="h-4 w-4 text-gray-400" data-id="element-882" />
                        {location}
                      </button>)}
                  </div>}
            </div>

            <div className="relative lg:w-48" data-id="element-883">
              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none z-10" data-id="element-884" />
              <input ref={dateInputRef} type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} placeholder="Select date" className="w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#01AAE7] text-gray-900 transition-all cursor-pointer" data-id="element-885" />
              {selectedDate && <button onClick={e => {
              e.stopPropagation();
              setSelectedDate('');
            }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10" data-id="element-886">
                  <X className="h-4 w-4" data-id="element-887" />
                </button>}
            </div>

            <button onClick={() => setShowFiltersPanel(!showFiltersPanel)} className="relative flex items-center justify-center gap-2 px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all border border-gray-200 lg:w-auto" data-id="element-888">
              <SlidersHorizontal className="h-5 w-5" data-id="element-889" />
              {activeFiltersCount > 0 && <span className="absolute -top-1 -right-1 bg-[#01AAE7] text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-id="element-890">
                  {activeFiltersCount}
                </span>}
            </button>

            <button className="px-8 py-3 bg-gradient-to-r from-[#01AAE7] to-[#0199d6] text-white font-bold rounded-xl hover:from-[#0199d6] hover:to-[#0188c5] transition-all shadow-lg hover:shadow-xl lg:w-auto" data-id="element-891">
              Search
            </button>
          </div>

          {showFiltersPanel && <div className="mt-4 pt-4 border-t border-gray-100 bg-gray-50 rounded-xl p-4" data-id="element-892">
              <div className="flex items-center justify-between mb-3" data-id="element-893">
                <h4 className="font-bold text-gray-900" data-id="element-894">Advanced Filters</h4>
                <button onClick={() => {
              setSearchQuery('');
              setSelectedLocation('');
              setLocationSearch('');
              setSelectedDate('');
              setSelectedDistance('All Distances');
              setSelectedTerrain('All Terrains');
              setSelectedPrice('All Prices');
            }} className="text-sm text-[#01AAE7] hover:text-[#0199d6] font-semibold" data-id="element-895">
                  Clear All
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-896">
                {/* Distance, Terrain, Price filters */}
              </div>
            </div>}

          {/* Map Section */}
          <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-897">
            <div className="relative w-full h-64 bg-gray-100 rounded-xl overflow-hidden border border-gray-200" data-id="element-898">
              <div ref={mapRef} className="w-full h-full" style={{
              zIndex: 1
            }} data-id="element-899"></div>

              {!mapLoaded && <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10" data-id="element-900">
                  <div className="text-center" data-id="element-901">
                    <Locate className="h-12 w-12 text-gray-400 mx-auto mb-3 animate-pulse" data-id="element-902" />
                    <p className="text-sm text-gray-600 font-medium" data-id="element-903">
                      Loading map...
                    </p>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-904">
          {filteredEvents.length > 0 ? filteredEvents.map(event => <div key={event.id} className="group relative bg-white rounded-xl sm:rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-[#01AAE7] transition-all duration-300" data-id="element-905">
                <div className="h-40 sm:h-48 relative overflow-hidden bg-gray-200" data-id="element-906">
                  <img src={event.image} alt={event.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" loading="lazy" data-id="element-907" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-908"></div>
                </div>

                <div className="p-5 sm:p-6 bg-gradient-to-b from-white to-gray-50" data-id="element-909">
                  <div className="flex items-center gap-2 mb-3 sm:mb-4" data-id="element-910">
                    <div className="flex items-center gap-1.5 sm:gap-2 bg-blue-50 text-[#01AAE7] px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg" data-id="element-911">
                      <Calendar className="h-3.5 sm:h-4 w-3.5 sm:w-4" data-id="element-912" />
                      <span className="text-xs sm:text-sm font-bold" data-id="element-913">
                        {event.displayDate}
                      </span>
                    </div>
                  </div>

                  <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-2 sm:mb-3 group-hover:text-[#0E578E] transition-colors leading-tight" data-id="element-914">
                    {event.title}
                  </h3>

                  <div className="flex items-center gap-2 text-gray-600 mb-4 sm:mb-6" data-id="element-915">
                    <MapPin className="h-3.5 sm:h-4 w-3.5 sm:w-4 text-gray-400 flex-shrink-0" data-id="element-916" />
                    <span className="text-xs sm:text-sm font-medium" data-id="element-917">
                      {event.location}
                    </span>
                  </div>

                  <Link to={`/events/${event.id}`} className="w-full text-white bg-gradient-to-r from-[#01AAE7] to-[#0199d6] rounded-xl hover:from-[#0199d6] hover:to-[#0188c5] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 py-3 sm:py-3.5 px-4 font-bold text-sm sm:text-base text-center block" data-id="element-918">
                    View Event
                  </Link>
                </div>
              </div>) : <div className="col-span-full text-center py-12 sm:py-16" data-id="element-919">
              <div className="text-gray-400 mb-4" data-id="element-920">
                <Search className="h-12 sm:h-16 w-12 sm:w-16 mx-auto mb-4" data-id="element-921" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2" data-id="element-922">
                No events found
              </h3>
              <p className="text-sm sm:text-base text-gray-500 mb-4" data-id="element-923">
                Try adjusting your search filters
              </p>
              <button onClick={clearAllFilters} className="px-5 sm:px-6 py-2 bg-[#01AAE7] text-white font-semibold rounded-lg hover:bg-[#0199d6] transition-colors text-sm sm:text-base" data-id="element-924">
                Clear All Filters
              </button>
            </div>}
        </div>

        {/* Pagination */}
        {filteredEvents.length > 0 && <div className="mt-8 sm:mt-10 flex justify-center lg:justify-end" data-id="element-925">
            <div className="inline-flex items-center gap-1 sm:gap-2 bg-white rounded-xl shadow-lg border border-gray-200 p-1.5 sm:p-2" data-id="element-926">
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all" data-id="element-927">
                <ChevronLeft className="h-4 sm:h-5 w-4 sm:w-5" data-id="element-928" />
              </button>

              <div className="flex items-center gap-0.5 sm:gap-1 px-1" data-id="element-929">
                <button onClick={() => setCurrentPage(1)} className={`min-w-[32px] sm:min-w-[40px] h-8 sm:h-10 px-2 sm:px-3 flex items-center justify-center rounded-lg text-xs sm:text-sm font-bold transition-all ${currentPage === 1 ? 'bg-gradient-to-br from-[#0E578E] to-[#01AAE7] text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-930">
                  1
                </button>
                <button onClick={() => setCurrentPage(2)} className={`min-w-[32px] sm:min-w-[40px] h-8 sm:h-10 px-2 sm:px-3 flex items-center justify-center rounded-lg text-xs sm:text-sm font-bold transition-all ${currentPage === 2 ? 'bg-gradient-to-br from-[#0E578E] to-[#01AAE7] text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-931">
                  2
                </button>
                <button onClick={() => setCurrentPage(3)} className={`min-w-[32px] sm:min-w-[40px] h-8 sm:h-10 px-2 sm:px-3 flex items-center justify-center rounded-lg text-xs sm:text-sm font-bold transition-all ${currentPage === 3 ? 'bg-gradient-to-br from-[#0E578E] to-[#01AAE7] text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-932">
                  3
                </button>
                <span className="px-1 sm:px-2 text-gray-400 text-xs sm:text-sm font-bold" data-id="element-933">
                  •••
                </span>
                <button onClick={() => setCurrentPage(5)} className={`min-w-[32px] sm:min-w-[40px] h-8 sm:h-10 px-2 sm:px-3 flex items-center justify-center rounded-lg text-xs sm:text-sm font-bold transition-all ${currentPage === 5 ? 'bg-gradient-to-br from-[#0E578E] to-[#01AAE7] text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-934">
                  5
                </button>
              </div>

              <button onClick={() => setCurrentPage(Math.min(5, currentPage + 1))} disabled={currentPage === 5} className="w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all" data-id="element-935">
                <ChevronRight className="h-4 sm:h-5 w-4 sm:w-5" data-id="element-936" />
              </button>
            </div>
          </div>}
      </div>
    </div>;
}